import { faCar, faPerson, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment, useContext, useRef, useState } from 'react';
import Button from 'components/ui/Button/Button';
import { showError } from 'utils/utils';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { createMapDirection } from 'services/hotel';
import PdfPrintOrSend from '../../Result/Modals/PdfPrintOrSend';
import { CreateProposalResponse } from 'ts/hotel/interfaces/Generic';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import MaximizeModal from 'components/shared/MaximizeModal';
import ScreenshotSupport from 'components/shared/ScreenshotSupport';
import useDraggableModal from 'hooks/useDraggableModal';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { getKey } from 'utils/hotel';
import { MainContext } from 'contexts/MainContext';
import { imgWaypointA, imgWaypointB } from 'constants/generic';
import { HotelConfigContext } from 'contexts/HotelConfigContext';

interface ReportModalGoogleProps {
  onClose: () => void;
  directions: any;
}

const ReportModalGoogle: FC<ReportModalGoogleProps> = ({ onClose, directions }) => {
  const [walking] = useState<google.maps.DirectionsLeg>(directions.walking.routes[0].legs[0]);
  const [driving] = useState<google.maps.DirectionsLeg>(directions.driving.routes[0].legs[0]);
  const [directionResponse, setDirectionResponse] = useState<CreateProposalResponse>();
  const {
    t } = useTranslation();
  const { hotelIndex } = useContext(HotelConfigContext);
  const { modalRef,
    modalStyle,
    dragHandleProps,
    isDragging } = useDraggableModal(true);
  const modalOverlayRef = useRef<HTMLDivElement>(null);

  const [loadingPrint,
    setLoadingPrint] = useState(false);

  const onClickOverlay = (event: any) => {
    if (event.target.classList.contains('modal-overlay')) {
      onClose();
    }
  };

  const onClickPrint = async () => {
    setLoadingPrint(true);

    const span = document.createElement('span');
    ReactDOM.render(<Content driving={driving} walking={walking} />,
    span);

    var params = {
      searchToken: sessionStorage.getItem(getKey('searchTokenHotels', hotelIndex)) || '',
      html: btoa(encodeURIComponent(span.innerHTML)),
    };

    const response = await createMapDirection(params);

    if (response && !response.hasErrors) {
      setDirectionResponse(response);
    } else if (response?.hasErrors) {
      console.log('Hotels/CreateMapDirectionsProposal ' + response?.errorMessage);
      showError(t('something_went_wrong'));
    }

    setLoadingPrint(false);
  };

  return (
    <div ref={modalOverlayRef} className="modal-overlay modal-maximizable" onClick={onClickOverlay}>
      <div ref={modalRef} className="modal active" style={modalStyle}>
        <div
          className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <div className="title text-lg">Map route</div>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={() => onClose()} />
          </div>
        </div>
        <div className="modal-content">
          <Content driving={driving} walking={walking} />
        </div>
        <div className="modal-footer">
          <Button
            title="Create Map Route"
            className={'btn btn-primary ml-0' + (!directionResponse ? ' mr-a' : '')}
            onClick={onClickPrint}
            disabled={loadingPrint}
          />
          <div className="mr-a">
            <PdfPrintOrSend
              show={!!directionResponse}
              email={directionResponse?.email}
              pdfUrl={directionResponse?.pdfUrl}
            />
          </div>
          <Button title="Close" className="btn btn-default" onClick={() => onClose()} />
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(ReportModalGoogle, 'ReportModalGoogle');

interface ContentProps {
  driving: google.maps.DirectionsLeg;
  walking: google.maps.DirectionsLeg;
}

const Content: FC<ContentProps> = ({ driving, walking }) => {
  return (
    <Fragment>
      <Ways leg={driving} icon={faCar} title="Driving" />
      <div style={{ margin: '30px 0', borderBottom: '1px solid #ddd' }}></div>
      <Ways leg={walking} icon={faPerson} title="Walking" />
    </Fragment>
  );
};

interface WaysProps {
  leg: google.maps.DirectionsLeg;
  icon: IconProp;
  title: string;
}
const Ways: FC<WaysProps> = ({ leg, icon, title }) => {
  const { initSiteData } = useContext(MainContext);
  const googleMapsKey = initSiteData?.apiKey || '';
  const generateMapUrl = (
    size: { x: number; y: number },
    markers: { lat: number; lng: number; label: string; color: string }[] = [],
    pathStr?: string
  ) => {
    const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap';
    const genericUrl = `?key=${googleMapsKey}&size=${size.x}x${size.y}`;

    const markersUrl =
      markers.length > 0
        ? markers
            .map(
              (marker) =>
                `markers=color:${encodeURIComponent(marker.color)}%7Clabel:${encodeURIComponent(
                  marker.label
                )}%7C${marker.lat},${marker.lng}`
            )
            .join('&')
        : '';

    const pathUrl = pathStr ? `&path=weight:5|enc:${encodeURIComponent(pathStr)}` : '';

    return baseUrl + genericUrl + (markersUrl ? '&' + markersUrl : '') + pathUrl;
  };

  const GetGoogleFullEncodePath = (stepsArr: any) => {
    const polyOptions = { strokeColor: '#000000' };
    const poly = new google.maps.Polyline(polyOptions);
    const path = poly.getPath();

    for (let i = 0; i < stepsArr.length; i++) {
      for (let j = 0; j < stepsArr[i].path.length; j++) {
        path.push(stepsArr[i].path[j]);
      }
    }
    const encodeString = google.maps.geometry.encoding.encodePath(path);

    if (encodeString.length > 1800) {
      const pathShort = [];

      for (let i = 0; i < stepsArr.length; i++) {
        pathShort.push(stepsArr[i].path[0]);
        pathShort.push(stepsArr[i].path[stepsArr[i].path.length - 1]);
      }
      return google.maps.geometry.encoding.encodePath(pathShort);
    } else {
      return encodeString;
    }
  };

  const getUrlMap = () => {
    return generateMapUrl(
      { x: 550, y: 200 },
      [
        {
          lat: leg.start_location.lat(),
          lng: leg.start_location.lng(),
          label: 'A',
          color: 'green',
        },
        { lat: leg.end_location.lat(), lng: leg.end_location.lng(), label: 'B', color: 'red' },
      ],
      GetGoogleFullEncodePath(leg.steps)
    );
  };

  return (
    <div style={{ lineHeight: 1.5, padding: '0 10px' }}>
      <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 10 }}>
        <FontAwesomeIcon style={{ width: 16 }} icon={icon} /> {title}
        <span> {leg.distance?.text}</span> /<span> {leg.duration?.text}</span>
      </div>
      <div style={{ margin: '10px 0' }}>
        <img style={{ marginRight: 10 }} src={imgWaypointA} alt="A" />
        <span>{leg.start_address}</span>
      </div>
      {leg.steps.map((step, index) => (
        <div style={{ display: 'flex', justifyContent: 'space-between' }} key={index}>
          <div>
            <span style={{ marginRight: 10 }}>{index + 1}.</span>
            <span dangerouslySetInnerHTML={{ __html: step.instructions }}></span>
          </div>
          <div>{step.distance?.text}</div>
        </div>
      ))}
      <div style={{ margin: '10px 0' }}>
        <img style={{ marginRight: 10 }} src={imgWaypointB} alt="B" />
        <span>{leg.end_address}</span>
      </div>
      <div style={{ marginBottom: 5 }}>
        <img src={getUrlMap()} style={{ height: 200, width: 550 }} alt="" />
      </div>

      <a
        href={`https://www.google.com/maps/dir/${leg.start_location.lat()},${leg.start_location.lng()}/${leg.end_location.lat()},${leg.end_location.lng()}`}
        target="_blank"
        rel="noreferrer"
      >
        Show directions on Google
      </a>
    </div>
  );
};
